// Ce que les écrans de pointage — celui du salarié et celui de la RH — lisent
// de la même façon : une heure dans le fuseau qu'Odoo a retenu, un jour en
// clair, une période de semaine ou de mois.

// Heure locale d'un horodatage ISO, dans le fuseau que la route a retenu.
// `timeZone` est passé explicitement : sans lui, l'affichage suivrait celui du
// navigateur, qui n'est pas forcément celui du salarié dans Odoo.
export const heureLocale = (iso, fuseau, langue) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(langue || 'fr', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: fuseau || 'UTC',
    }).format(new Date(iso));
  } catch (e) {
    return null;
  }
};

// Date puis heure d'un horodatage ISO, dans le fuseau que la route a retenu.
// Rendues séparément : la phrase qui les porte les assemble comme sa langue le
// dit (« le {{date}} à {{heure}} »). `null` si l'horodatage n'est pas lisible.
export const dateHeureLocale = (iso, fuseau, langue) => {
  const heure = heureLocale(iso, fuseau, langue);
  if (!heure) return null;
  try {
    const date = new Intl.DateTimeFormat(String(langue || 'fr').startsWith('en') ? 'en-GB' : langue || 'fr', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: fuseau || 'UTC',
    }).format(new Date(iso));
    return { date, heure };
  } catch (e) {
    return null;
  }
};

// Libellé d'un jour depuis sa date `YYYY-MM-DD`. Les trois nombres sont
// construits en heure LOCALE du navigateur, jamais par `new Date('2026-08-24')`
// qui parse en UTC : à l'ouest de Greenwich, ce raccourci afficherait la veille.
export const jourLisible = (date, langue) => {
  const [annee, mois, jour] = String(date).split('-').map(Number);
  if (!annee || !mois || !jour) return date;
  return new Intl.DateTimeFormat(langue || 'fr', {
    weekday: 'long', day: '2-digit', month: '2-digit',
  }).format(new Date(annee, mois - 1, jour));
};

// Arithmétique de dates en UTC : le calendrier ne dépend ni du fuseau ni de
// l'heure d'été du navigateur.
const enDate = (jour) => new Date(`${jour}T00:00:00Z`);
const enJour = (date) => date.toISOString().slice(0, 10);
const decalerJours = (jour, jours) => {
  const date = enDate(jour);
  date.setUTCDate(date.getUTCDate() + jours);
  return enJour(date);
};

/**
 * La période de `type` (`semaine` ou `mois`) qui contient `ancre`, arrêtée à
 * `aujourdhui` : Odoo refuse une période dans le futur (`periode_invalide`).
 */
export const periodeDe = (type, ancre, aujourdhui) => {
  const date = enDate(ancre);
  let du;
  let fin;
  if (type === 'mois') {
    du = enJour(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
    fin = enJour(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)));
  } else {
    du = decalerJours(ancre, -((date.getUTCDay() + 6) % 7));
    fin = decalerJours(du, 6);
  }
  return { du, au: fin < aujourdhui ? fin : aujourdhui };
};

/** L'ancre de la période d'à côté : une semaine, ou le premier d'un autre mois. */
export const ancreVoisine = (type, ancre, pas) => {
  if (type === 'mois') {
    const date = enDate(ancre);
    return enJour(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + pas, 1)));
  }
  return decalerJours(ancre, 7 * pas);
};
